import { MAX_QUALITIES, QUALITIES, QUALITY_COST, qualityById } from "@personnel/rules-leviathan";
import { FACTIONS } from "@personnel/setting";
import type { LeviathanCaptain } from "@personnel/rules-leviathan";

interface QualityPickerProps {
  captain: LeviathanCaptain;
  onToggle: (id: string) => void;
}

const TAG_LABEL: Record<string, string> = {
  weaver: "Weaver",
  physical: "Physical",
};

/**
 * Explains why a Quality cannot be added right now. Returns null when it can.
 * Selected Qualities are never blocked -- a player must always be able to
 * remove something, even if the current combination is illegal.
 */
function blockedReason(captain: LeviathanCaptain, id: string): string | null {
  const quality = qualityById(id);
  if (quality === undefined) return null;
  if (captain.qualities.includes(id)) return null;

  if (quality.tag !== null) {
    const conflict = captain.qualities
      .map((selected) => qualityById(selected))
      .find((selected) => selected?.tag === quality.tag);
    if (conflict !== undefined) {
      return `Already have a ${TAG_LABEL[quality.tag]} Quality (${conflict.name})`;
    }
  }

  if (quality.exemptFromLimit !== true) {
    const counted = captain.qualities.filter(
      (selected) => qualityById(selected)?.exemptFromLimit !== true,
    ).length;
    if (counted >= MAX_QUALITIES) return `Limit of ${MAX_QUALITIES} Qualities reached`;
  }

  for (const requirement of quality.requires ?? []) {
    if (
      requirement.attribute === "rank" &&
      requirement.minimum !== undefined &&
      captain.attributes.rank < requirement.minimum
    ) {
      return `Requires Rank ${requirement.minimum}`;
    }

    if (requirement.faction !== undefined && captain.faction !== requirement.faction) {
      return `${FACTIONS[requirement.faction].name} only`;
    }
  }

  return null;
}

export function QualityPicker({ captain, onToggle }: QualityPickerProps) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {QUALITIES.map((quality) => {
        const selected = captain.qualities.includes(quality.id);
        const blocked = blockedReason(captain, quality.id);
        const disabled = blocked !== null;

        return (
          <li key={quality.id}>
            <button
              type="button"
              onClick={() => onToggle(quality.id)}
              disabled={disabled}
              aria-pressed={selected}
              title={disabled ? blocked : quality.description}
              className={`flex h-full w-full flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                selected
                  ? "border-leviathan-600 bg-leviathan-600/10"
                  : disabled
                    ? "cursor-not-allowed border-void-800/50 opacity-45"
                    : "border-void-800 hover:border-leviathan-600/60"
              }`}
            >
              <span className="flex w-full items-center gap-2">
                <span className="font-display text-sm font-semibold text-void-200">
                  {quality.name}
                </span>
                {quality.tag !== null && (
                  <span className="rounded-full border border-void-700 px-1.5 text-[10px] tracking-wide text-void-400 uppercase">
                    {TAG_LABEL[quality.tag]}
                  </span>
                )}
                {quality.exemptFromLimit === true && (
                  <span className="rounded-full border border-void-700 px-1.5 text-[10px] tracking-wide text-void-400 uppercase">
                    extra
                  </span>
                )}
                <span className="ml-auto shrink-0 text-xs text-void-700 tabular-nums">
                  {QUALITY_COST}
                </span>
              </span>

              <span className="line-clamp-2 text-xs leading-relaxed text-void-400">
                {blocked ?? quality.description}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
